import { QUESTIONNAIRE_LIMITS, type QuestionKind } from './questionnaire';
import type { AttemptOutcome } from './training-attempt';

/** `ungraded` is an answer on an attempt that is not submitted yet; `pending` waits for a grader. */
export const ANSWER_GRADE_STATES = {
  ungraded: 'ungraded',
  auto: 'auto',
  pending: 'pending',
  graded: 'graded',
} as const;
export type AnswerGradeState = (typeof ANSWER_GRADE_STATES)[keyof typeof ANSWER_GRADE_STATES];

export const TRAINING_GRADE_LIMITS = {
  // A request may carry every question of the largest questionnaire.
  maxGradesPerRequest: QUESTIONNAIRE_LIMITS.maxQuestions,
  maxNoteLength: 2000,
} as const;

/** One submitted attempt that waits for a grader, oldest first. It carries no answer text. */
export interface ReviewQueueItem {
  attemptId: string;
  assigneeName: string;
  assignmentTitle: string;
  questionnaireTitle: string;
  versionNumber: number;
  attemptNumber: number;
  submittedAt: string;
  submittedLate: boolean;
  /** How many written answers still have no points. */
  pendingAnswers: number;
}

export interface GradableOption {
  id: string;
  position: number;
  text: string;
  isCorrect: boolean;
}

export interface GradableAnswer {
  selectedOptionIds: string[];
  text: string | null;
  /** `null` while a grader still owes the answer points. */
  awardedPoints: number | null;
  gradeState: AnswerGradeState;
  graderNote: string | null;
  /** Who gave the points, when user-service can still say; `null` for an answer nobody has marked. */
  gradedByName: string | null;
}

/**
 * What a grader is served. Unlike a taker's question it carries the key and the
 * model answer, which is why it is its own type and never extends `TakerQuestion`.
 */
export interface GradableQuestion {
  id: string;
  position: number;
  kind: QuestionKind;
  prompt: string;
  points: number;
  /** Guidance for the grader; only a short-text question has one. */
  modelAnswer: string | null;
  options: GradableOption[];
  /** `null` only for an attempt that has no row for the question. */
  answer: GradableAnswer | null;
}

export interface GradableAttempt {
  id: string;
  assigneeName: string;
  assignmentTitle: string;
  questionnaireTitle: string;
  versionNumber: number;
  attemptNumber: number;
  submittedAt: string | null;
  submittedLate: boolean;
  outcome: AttemptOutcome;
  passMarkPercent: number;
  maxPoints: number;
  /** Only once the outcome is decided. `maxPoints` is the sum of the questions this attempt drew. */
  scorePoints: number | null;
  scorePercent: number | null;
  /** The questionnaire's total marks; `null` means points as written. */
  totalMarks: number | null;
  /** Only once the outcome is decided, and only with `totalMarks`. */
  scoreMarks: number | null;
  gradedAt: string | null;
  gradedByName: string | null;
  pendingAnswers: number;
  questions: GradableQuestion[];
}

export interface GradeAnswerInput {
  questionId: string;
  /** A whole number from 0 to the question's points. */
  awardedPoints: number;
  /**
   * The points the grader saw when the page loaded (`null` for none yet). If the answer
   * holds anything else now, somebody else marked it first and the grade is refused (409).
   */
  expectedPoints: number | null;
  note?: string | null;
}

export interface GradeAttemptInput {
  grades: GradeAnswerInput[];
}

/** What a grade returns: the attempt, and whether the person was queued a result message. */
export interface GradeAttemptResult extends GradableAttempt {
  /** False when this grade did not decide the attempt, or the person could not be mailed. */
  resultQueued: boolean;
}

/** The grader, taken from the token and never from a body. */
export interface ReviewCaller {
  graderId: string;
}

export interface ReviewAttemptPayload extends ReviewCaller {
  attemptId: string;
}

export interface GradeAttemptPayload extends ReviewAttemptPayload {
  input: GradeAttemptInput;
}
